import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import HotelSettingsCompetitorsController from 'lumen/controllers/hotel/settings/competitors';
import type {
  BenchmarkInsightCompetitorsCompetitor,
  BenchmarkInsightCompetitorsCompset,
  BeniSmartCompset,
} from 'lumen/models';
import type HotelSettingsCompetitorsBenchmarkRoute from 'lumen/routes/hotel/settings/competitors/benchmark';
import {
  BENI_COMPETITORS_TABS,
  type CompetitorsBenchmarkTab,
  ROUTE_BENCHMARK_INSIGHT_COMPSET_BUILDER,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { ModelFrom } from 'lumen/utils/routes';

export default class HotelSettingsCompetitorsBenchmarkController extends HotelSettingsCompetitorsController {
  declare model: ModelFrom<HotelSettingsCompetitorsBenchmarkRoute>;

  // eslint-disable-next-line ember/no-unused-services
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeTab: CompetitorsBenchmarkTab = BENI_COMPETITORS_TABS.COMPSET;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get buttonEditCompsetDisabledTooltip() {
    if (!this.isButtonEditCompsetDisabled || !this.compset) {
      return null;
    }

    const daysUntilAvailable = dayjs
      .utc(this.compset.cantChangeUntil)
      .diff(dayjs.utc().startOf('day'), 'day');
    return {
      title: this.intl.t(
        'settings.competitors.benchmark.compset.edit_locked_tooltip.title',
        {
          count: daysUntilAvailable,
        },
      ),
      description: this.intl.t(
        'settings.competitors.benchmark.compset.edit_locked_tooltip.description',
        { count: 30 },
      ),
    };
  }

  get buttonEditCompsetLabel(): string {
    if (this.model.compset?.last?.value?.compset.length) {
      return this.intl.t(
        'settings.competitors.benchmark.compset.update_compset',
      );
    }

    return this.intl.t(
      'settings.competitors.benchmark.compset.empty_state.action',
    );
  }

  get competitors(): BenchmarkInsightCompetitorsCompetitor[] {
    const { compset } = this;

    if (!compset) {
      return [];
    }

    return compset.compset;
  }

  get compset(): BenchmarkInsightCompetitorsCompset | null {
    return this.model.compset.last?.value ?? null;
  }

  get isButtonEditCompsetDisabled(): boolean {
    if (!this.compset) {
      return true;
    }

    return !this.compset.isCompsetEditable;
  }

  get isLoading(): boolean {
    return this.model.compset.isRunning || this.model.smartCompset.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pageTitle() {
    const titleKeyMap = {
      [BENI_COMPETITORS_TABS.COMPSET]: tKey('settings.competitors.compset'),
      [BENI_COMPETITORS_TABS.PORTFOLIO_COMPSET]: tKey(
        'settings.competitors.portfolio_compset',
      ),
      [BENI_COMPETITORS_TABS.SMART_COMPSET]: tKey(
        'settings.competitors.smart_compset',
      ),
    };

    return this.intl.t(titleKeyMap[this.activeTab]);
  }

  get showOldCompsetContent(): boolean {
    // Currently, we have 3 Benchmark tabs on the competitors route. These all navigate to the same child route,
    // but with a different `activeTab` QP. Ideally this should be reworked into 3 different child routes.
    // There's no time to refactor all 3 tabs right now, however we are reworking 1 of them: the compset tab.
    // I've added a new child route for the compset tab, which means that for now, we have
    // a mix of both approaches. Therefore, we need this getter to check if we can just render an outlet, or not.
    return (
      this.router.currentRouteName ===
      'hotel.settings.competitors.benchmark.index'
    );
  }

  get smartCompset(): BeniSmartCompset | null {
    return this.model.smartCompset.last?.value ?? null;
  }

  @action onClickEditCompset(): void {
    this.router.transitionTo(ROUTE_BENCHMARK_INSIGHT_COMPSET_BUILDER);

    this.usageAnalytics.track('Benchmark Compset Editing', {
      eventTrigger: 'benchmark-insight-compset-settings',
    });
  }

  @action
  setActiveTab(id: CompetitorsBenchmarkTab): void {
    this.activeTab = id;
  }
}
